import type { ReactElement } from 'react';
import { AlertTriangle, Bell, CheckCircle2, EyeOff, MessageSquare, ShieldCheck, Trash2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { formatDate } from '@xdocs/i18n';
import type { Notification } from '@xdocs/schemas';
import { cn, useI18n } from '@xdocs/ui';
import { GroupExpandToggle } from './GroupExpandToggle.js';
import { useNotificationsT } from './useNotificationsT.js';
import styles from './NotificationItem.module.css';

/** `refType` → best-effort route. Anything not recognized still marks read
 * but doesn't navigate (graceful "no longer available" is the item's own
 * `deletedTarget` copy, shown when there's simply nothing to open). Shared
 * by both the topbar bell panel (`NotificationsBell.tsx`) and the standalone
 * `/notifications` feed page (`routes/notifications/NotificationsPage.tsx`)
 * — same rows, two different containers (design/app/32-notifications.html
 * is the dedicated feed page; the bell is a lighter anchored preview of the
 * same data). */
export function routeForRef(refType: string | null, refId: string | null): string | null {
  if (!refType || !refId) return null;
  if (refType === 'document') return `/documents/${refId}`;
  // F27/F62/F69/F74 (share approved/rejected, takedown, cleared-after-review)
  // — `refType==='share'` notifications carry `shares.id`; the public
  // discover view is the correct landing page for all of them.
  if (refType === 'share') return `/discover/${refId}`;
  return null;
}

/**
 * `notification.type` → a per-kind icon (mockup 32-notifications.html: each
 * row carries a colored icon tile, not a bare text line). `type` is an open
 * `z.string()` domain-event id (comms.ts), not a closed enum — this maps the
 * known writers (`packages/core/src/notifications.ts` call sites: render/
 * ingest workers, moderation/admin/social handlers) with an exact match
 * first, a `.takedown`/`.failed` suffix fallback for the templated variants
 * (`` `${targetType}.takedown` ``), and `Bell` as the safe default for any
 * future/unrecognized event — never a crash on an unknown string.
 */
const NOTIFICATION_ICON_BY_TYPE: Record<string, LucideIcon> = {
  'job.done': CheckCircle2,
  'share.approved': CheckCircle2,
  'share.rejected': XCircle,
  'comment.new': MessageSquare,
  'asset.removed': Trash2,
  'asset.unpublished': EyeOff,
  'moderation.queue.cleared': ShieldCheck,
};

export function iconForNotificationType(type: string): LucideIcon {
  const exact = NOTIFICATION_ICON_BY_TYPE[type];
  if (exact) return exact;
  if (type.endsWith('.takedown') || type.endsWith('.failed')) return AlertTriangle;
  return Bell;
}

export interface NotificationItemProps {
  item: Notification;
  onOpen: (item: Notification) => void;
  expanded: boolean;
  onToggleExpand: (notificationId: string) => void;
  /** Optional class for the outer `<li>` — lets a consumer (e.g. the
   * `/notifications` page's flush single-card list vs. the bell panel's
   * gapped rows) style the row boundary without this shared component
   * hardcoding either container's layout. */
  wrapperClassName?: string | undefined;
  /** `'panel'` (default): the bell's gapped, individually-rounded rows.
   * `'flush'`: design/app/32-notifications.html's `/notifications` page —
   * square-cornered rows flush against each other inside one rounded card
   * (the card itself owns the 14px radius; see `NotificationsPage.module.css`). */
  variant?: 'panel' | 'flush' | undefined;
}

/**
 * F33 grouping polish: a burst sharing a `groupingKey` already collapses to
 * ONE row server-side (`groupCount`, `title`/`body` pre-rendered to
 * summarize it, e.g. title "12 people liked…" / body the joined detail).
 * `groupCount === 1` renders flat (body always visible, no toggle) exactly
 * as before; `groupCount > 1` collapses the detail `body` behind a
 * `<GroupExpandToggle>` — the "+N more" affordance FEATURES.md's F33 calls
 * for — so a long comma-joined name list doesn't dominate the panel by
 * default. The row's own click-to-open/mark-read target became a nested
 * `<button>` (title + timestamp only) rather than wrapping the whole `<li>`,
 * so the sibling toggle button is never a button-inside-a-button (invalid
 * HTML / broken hit-testing).
 */
export function NotificationItem({
  item,
  onOpen,
  expanded,
  onToggleExpand,
  wrapperClassName,
  variant = 'panel',
}: NotificationItemProps): ReactElement {
  const { t } = useNotificationsT();
  const { locale } = useI18n();
  const unread = item.readAt === null;
  const isGrouped = item.groupCount > 1;
  const showBody = !isGrouped || expanded;
  const Icon = iconForNotificationType(item.type);

  return (
    <li className={cn(wrapperClassName)}>
      <div className={styles['item']} data-unread={unread} data-variant={variant}>
        <span className={styles['itemIconTile']} aria-hidden="true">
          <Icon size={16} />
        </span>
        <span className={unread ? styles['itemUnreadDot'] : styles['itemUnreadDotHidden']} aria-hidden="true" />
        <button type="button" className={styles['itemOpenButton']} onClick={() => onOpen(item)}>
          <span className={styles['itemBody']}>
            <span className={styles['itemTitle']}>{item.title}</span>
            {showBody && <span className={styles['itemText']}>{item.body}</span>}
            <span className={styles['itemTime']}>{formatDate(locale, item.createdAt, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          </span>
        </button>
        {isGrouped && (
          <GroupExpandToggle
            expanded={expanded}
            countLabel={t('notifications:item.groupCount', { count: item.groupCount })}
            expandLabel={t('notifications:item.groupExpand')}
            collapseLabel={t('notifications:item.groupCollapse')}
            onToggle={() => onToggleExpand(item.id)}
          />
        )}
      </div>
    </li>
  );
}
